import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { MoneyService } from '../money/money.service';
import { SystemService } from '../../../../../system/system.service';
import { LookupChange, LookupEntityConf, SystemDTO } from '../../../../../system/system-dtos';
import { isDefined } from '../../../../utils/utils';
import { MaintainService } from '../../../../views/maintain/maintain.service';
import { PageResponseDTO } from '../../../../../common/paging-dto';
import { StringUtils } from '../../../../../common/string-utils';

@Injectable()
export class LookupService {
  public timezone: string;

  lookupChange: Subject<LookupChange> = new Subject<LookupChange>();

  lookupMap: Map<string, LookupEntityConf>;

  constructor(
    private moneyService: MoneyService,
    private systemService: SystemService,
    private maintainService: MaintainService
  ) {
    this.systemService.systemTimeZoneSubject.subscribe((timezone: string) => {
      this.timezone = timezone;
    });

    this.systemService.getSystem().subscribe((system: SystemDTO) => {
      if (isDefined(system.lookups)) {
        this.lookupMap = new Map(system.lookups.map((i) => [i.name, i]));
      }
    });
  }

  public getLookupChangeSubject() {
    return this.lookupChange;
  }

  public getConfByLookupCode(lookupcode: string): LookupEntityConf {
    return this.lookupMap.get(lookupcode);
  }

  /**
   * Fetches a single page of a server-paged lookup (FieldType.paged_lookup), optionally
   * restricted to a single ID. Used by both LookupPagedFormFieldComponent (to resolve the
   * label of the currently selected entity) and LookupPagedModalComponent (to page/search).
   *
   * Unlike the legacy `lookup` type, this never falls back to pulling the whole collection:
   * every call hits the server for just the page (or the single ID) it needs.
   */
  public searchPaged(
    url: string,
    offset: number,
    perPage: number,
    id?: string,
    context: any = {}
  ): Observable<PageResponseDTO<any>> {
    let extraParams = new HttpParams();
    if (id) {
      extraParams = extraParams.set('id', id);
    }

    return this.maintainService.selectAllData(this.resolveLookupUrl(url, context), { offset, perPage }, extraParams);
  }

  private resolveLookupUrl(url: string, context: any): string {
    let resolvedUrl = url;

    // {id} in a lookup URL refers to the entity currently open in the browser (e.g. a player),
    // not to a field on the form itself - mirrors the legacy LookupModalComponent behaviour.
    if (resolvedUrl && resolvedUrl.includes('{id}')) {
      const playerId = this.extractPlayerIdFromUrl();
      if (playerId !== undefined) {
        resolvedUrl = resolvedUrl.replace('{id}', playerId);
      }
    }

    return StringUtils.convert(resolvedUrl, context);
  }

  private extractPlayerIdFromUrl(): string | undefined {
    const currentUrl: string = window.location.href;
    if (!currentUrl.includes('/players/')) {
      return undefined;
    }

    const parts: string[] = currentUrl.split('/');
    for (let x = 0; x < parts.length; x++) {
      if (parts[x] === 'players') {
        return parts[x + 1];
      }
    }
    return undefined;
  }
}
